import React, { useEffect, useMemo, useRef, useState } from 'react';

export const useUserAction = () => {
  const [hasActionOccurred, setHasActionOccurred] = useState(false);
  const hasActionOccurredRef = useRef(false);

  useEffect(() => {
    const eventHandler = () => {
      if (hasActionOccurredRef.current) {
        return;
      }

      setHasActionOccurred(true);
      hasActionOccurredRef.current = true;

      // Cleanup: remove listeners after the first action
      window.removeEventListener('click', eventHandler);
      window.removeEventListener('touchstart', eventHandler);
      window.removeEventListener('scroll', eventHandler);
      window.removeEventListener('keydown', eventHandler);
    };

    // Add event listeners for various user actions
    window.addEventListener('click', eventHandler);
    window.addEventListener('touchstart', eventHandler);
    window.addEventListener('scroll', eventHandler);
    window.addEventListener('keydown', eventHandler);

    return () => {
      // Cleanup function
      window.removeEventListener('click', eventHandler);
      window.removeEventListener('touchstart', eventHandler);
      window.removeEventListener('scroll', eventHandler);
      window.removeEventListener('keydown', eventHandler);
    };
  }, []);

  return hasActionOccurred;
};

export function useInterval(callback, delay = 60000) {
  const savedCallback = useRef();

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    function func() {
      savedCallback.current();
    }
    if (delay !== null) {
      let id = setInterval(func, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

export function useScrollTrigger(scrollThreshold = 0) {
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!hasScrolled && window.scrollY > scrollThreshold) {
        setHasScrolled(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasScrolled, scrollThreshold]);

  return hasScrolled;
}

export function useInView(options = {}) {
  const { triggerOnce = false, ...observerOptions } = options;
  const [isInView, setIsInView] = useState(false);
  const elementRef = useRef(null);
  const hasTriggeredRef = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      const isIntersecting = entry.isIntersecting;

      // If triggerOnce is true and already triggered, don't update
      if (triggerOnce && hasTriggeredRef.current) {
        return;
      }

      setIsInView(isIntersecting);

      // Mark as triggered when element comes into view
      if (isIntersecting && triggerOnce) {
        hasTriggeredRef.current = true;
      }
    }, observerOptions);

    const currentElement = elementRef.current;

    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [observerOptions, triggerOnce]);

  return [elementRef, isInView];
}

export function useDarkModeFromQuery(options = {}) {
  const { syncHtmlClass = false } = options;

  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    // SSR/Non-browser safety
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const urlParams = new URLSearchParams(window.location.search);
    const dm = urlParams.get('dm');
    const disabledm = urlParams.get('disabledm');

    const applyMode = (enabled) => {
      setIsDarkMode(enabled);

      // Optional: just toggles a class so you can style via CSS
      if (syncHtmlClass) {
        document.documentElement.classList.toggle('dark', enabled);
      }
    };

    // dm=1 has priority over disabledm=1
    if (dm === '1') {
      applyMode(true);
      return; // no listener needed
    }

    if (disabledm === '1') {
      applyMode(false);
      return; // no listener needed
    }

    // Use system preference + listen to changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    // initial value
    applyMode(mediaQuery.matches);

    // update on system change
    const handleChange = (e) => applyMode(e.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [syncHtmlClass]);

  return isDarkMode;
}
